import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GuardianContext } from '../../components/GuardianProvider';
import { useCachedState } from '../../hooks/useCachedState';
import { useChat } from '../../hooks/useChat';
import { applyPanelTitles, DEFAULT_VIEW_STATE, placeItems, resolveActivePanelId } from '../layout';
import { dashboardPanelId, targetPanelId } from '../placement';
import type { DashboardPanel } from '../types/panel';
import type { PanelItem, WidgetId } from '../types/panelItem';
import type { PanelViewState, PlacedItem } from '../types/panelModel';
import type { PanelId, PanelPlacement } from '../types/panelPlacement';
import type { DashboardPanelInput, PanelStore } from '../types/panelStore';

export const PanelStoreContext = createContext<PanelStore | null>(null);

const panelQueryKey = (conversationId: string) => ['panels', conversationId] as const;

export const PanelStoreProvider = ({ children }: { children: ReactNode }) => {
  const { conversationId, conversationScope } = useContext(GuardianContext);
  const [placed, setPlaced] = useState<PlacedItem[]>([]);
  // Route-driven dashboard panel. Ephemeral (not persisted): the dashboard route owns it.
  const [dashboardPanel, setDashboardPanelState] = useState<DashboardPanel | null>(null);
  // See PanelStore.animateLayout — every action below sets it alongside its own state so both land
  // in one commit; React batches them, so the DOM never sees a width change with a stale flag.
  const [animateLayout, setAnimateLayout] = useState(false);
  const { conversation } = useChat();

  const viewStateKey = useMemo(() => panelQueryKey(conversationId), [conversationId]);

  // Per-conversation panel layout, persisted with the conversation it belongs to.
  const [viewState, updateViewState] = useCachedState<PanelViewState>(viewStateKey, DEFAULT_VIEW_STATE, {
    // Same rule as the conversation it belongs to: a dashboard analysis is not persisted,
    // so its layout must not outlive it in localStorage either.
    shouldPersist: conversationScope === 'chat' && !conversation.isNew,
  });

  const registerWidget = useCallback((item: PanelItem, placement: PanelPlacement) => {
    // A widget arriving in a response opens the panel — that reveal is worth sliding.
    setAnimateLayout(true);
    setPlaced((prev) => {
      const index = prev.findIndex((entry) => entry.item.id === item.id);
      if (index === -1) {
        return [...prev, { item, placement }];
      }
      const next = [...prev];
      next[index] = { item, placement };
      return next;
    });
  }, []);

  const unregisterWidget = useCallback((id: WidgetId) => {
    // Widgets unregister when their message unmounts, i.e. on navigation — nothing to animate.
    setAnimateLayout(false);
    setPlaced((prev) => prev.filter((entry) => entry.item.id !== id));
  }, []);

  const closePanel = useCallback((id: PanelId) => {
    setAnimateLayout(true);
    // Drop every widget routed to this panel; the panel disappears once it has no items.
    setPlaced((prev) => prev.filter((entry) => targetPanelId(entry.placement, entry.item) !== id));
  }, []);

  /*
   * A newly extracted component opens the panel. It is the answer to what the user just asked —
   * a ticket the support agent has created, a chart it drew — so it must not land behind a
   * collapsed edge tab that has to be found and clicked.
   */
  const placedCount = placed.length;
  const lastPlacedCount = useRef(placedCount);
  useEffect(() => {
    const grew = placedCount > lastPlacedCount.current;
    lastPlacedCount.current = placedCount;
    if (grew && viewState.isCollapsed) {
      // registerWidget already armed the animation — this is the reveal it was armed for.
      updateViewState((prev) => ({ ...prev, isCollapsed: false }));
    }
  }, [placedCount, viewState.isCollapsed, updateViewState]);

  const panels = useMemo(() => {
    const widgetPanels = applyPanelTitles(placeItems(placed), viewState.titles);
    // Dashboard first, so it is the default active tab and always visible.
    return dashboardPanel ? [dashboardPanel, ...widgetPanels] : widgetPanels;
  }, [placed, viewState.titles, dashboardPanel]);
  const activePanelId = useMemo(
    () => resolveActivePanelId(panels, viewState.activePanelId),
    [panels, viewState.activePanelId],
  );

  const setDashboardPanel = useCallback((panel: DashboardPanelInput | null) => {
    // Pinned/unpinned by the dashboard route itself: a mode switch, so the panel snaps to its width.
    setAnimateLayout(false);
    setDashboardPanelState(
      panel
        ? {
            id: dashboardPanelId(panel.templateId),
            title: panel.title,
            kind: 'manual',
            source: 'dashboard',
            templateId: panel.templateId,
            content: panel.content,
          }
        : null,
    );
  }, []);

  const setActivePanel = useCallback(
    (id: PanelId) => updateViewState((prev) => ({ ...prev, activePanelId: id })),
    [updateViewState],
  );

  const renamePanel = useCallback(
    (id: PanelId, title: string) => {
      const trimmed = title.trim();
      updateViewState((prev) => {
        const titles = { ...prev.titles };
        if (trimmed) {
          titles[id] = trimmed;
        } else {
          delete titles[id];
        }
        return { ...prev, titles };
      });
    },
    [updateViewState],
  );

  const setCollapsed = useCallback(
    (collapsed: boolean) => {
      // The one deliberate open/close gesture — this is the transition the slide was designed for.
      setAnimateLayout(true);
      updateViewState((prev) => ({ ...prev, isCollapsed: collapsed }));
    },
    [updateViewState],
  );

  const value = useMemo<PanelStore>(
    () => ({
      panels,
      activePanelId,
      isCollapsed: viewState.isCollapsed,
      animateLayout,
      registerWidget,
      unregisterWidget,
      setActivePanel,
      renamePanel,
      setCollapsed,
      closePanel,
      setDashboardPanel,
    }),
    [
      panels,
      activePanelId,
      viewState.isCollapsed,
      animateLayout,
      registerWidget,
      unregisterWidget,
      setActivePanel,
      renamePanel,
      setCollapsed,
      closePanel,
      setDashboardPanel,
    ],
  );

  return <PanelStoreContext.Provider value={value}>{children}</PanelStoreContext.Provider>;
};
